import { AGE_GROUPS, getAudienceLabel } from '../segments';

export const GLOBAL_WELLNESS_HUB_SLUG = 'healthy-eating-wellness-guide';

/** Segment-specific copy for global healthy eating (diet only) */
export function getGlobalSegmentProfile(genderKey, ageKey) {
  const audience = getAudienceLabel(genderKey, ageKey);
  const isWomen = genderKey === 'women';
  const isYoung = ageKey === '18-34';
  const isMid = ageKey === '35-49';
  const isOlder = ageKey === '50-plus';

  let proteinRange = '1.2–1.6 g/kg';
  let portionNote =
    'Portions suit many adults roughly 55–85 kg—adjust for your weight, activity level, and appetite.';

  if (isYoung) {
    proteinRange = isWomen ? '1.2–1.6 g/kg' : '1.4–1.8 g/kg';
  } else if (isMid) {
    proteinRange = isWomen ? '1.4–1.7 g/kg' : '1.4–1.8 g/kg';
    portionNote =
      'Portions suit many adults around 60–85 kg—favor steady energy over aggressive crash diets.';
  } else {
    proteinRange = isWomen ? '1.4–1.8 g/kg' : '1.4–1.8 g/kg';
    portionNote =
      'Portions suit many adults 60–90 kg—prioritize protein, fiber, and hydration; lean out slowly with your clinician if needed.';
  }

  const dietWhoLead = isWomen
    ? `You are a woman (${AGE_GROUPS.find((a) => a.key === ageKey)?.label}) looking for balanced, practical meals you can cook at home anywhere in the world.`
    : `You are a man (${AGE_GROUPS.find((a) => a.key === ageKey)?.label}) looking for balanced, practical meals you can cook at home anywhere in the world.`;

  const nutritionPriorities = [];

  if (isWomen) {
    nutritionPriorities.push(
      'Iron and protein: pair legumes, leafy greens, and eggs (if you eat them) with vitamin C from citrus or peppers.',
      'Calcium and vitamin D for bone health—yogurt, milk, fortified plant milks, and sensible sun or supplements per your doctor.',
      'Eat enough across the day—skipping meals often leads to evening overeating.'
    );
    if (isYoung) {
      nutritionPriorities.push(
        'If you are pregnant, breastfeeding, or trying to conceive, do not follow this plan without your OB or dietitian—needs change completely.'
      );
    }
    if (isMid) {
      nutritionPriorities.push(
        'Perimenopause can shift sleep and weight—consistent protein, fiber, and movement help; hormones are a clinician conversation.'
      );
    }
    if (isOlder) {
      nutritionPriorities.push(
        'Bone and muscle: protein at every meal, strength work if you are cleared to exercise, and steady hydration year-round.'
      );
    }
  } else {
    nutritionPriorities.push(
      'Protein spread across meals (eggs, legumes, dairy, fish, or poultry if non-veg) to support muscle and fullness.',
      'Fiber-rich carbs and vegetables for heart and blood sugar health.',
      'Moderate alcohol—it adds empty calories and can disrupt sleep.'
    );
    if (isMid || isOlder) {
      nutritionPriorities.push(
        'Watch sodium and saturated fat from restaurant meals if blood pressure or cholesterol are concerns—annual labs with your doctor.'
      );
    }
    if (isOlder) {
      nutritionPriorities.push(
        'Favor grilled, baked, or sautéed dishes over deep-fried snacks for easier digestion and heart health.'
      );
    }
  }

  if (isYoung) {
    nutritionPriorities.push(
      'Build habits you can keep—regular meal times beat extreme rules you cannot sustain.'
    );
  }

  nutritionPriorities.push(
    'Shop local supermarkets, farmers markets, or online grocers—swap ingredients based on what is fresh and affordable near you.'
  );

  return {
    audience,
    dietWhoLead,
    nutritionPriorities,
    proteinRange,
    portionNote,
  };
}

export function globalDietSlug(dietType, genderKey, ageKey) {
  const prefix = dietType === 'vegetarian' ? 'vegetarian-healthy-diet' : 'non-vegetarian-healthy-diet';
  return `${prefix}-${genderKey}-${ageKey}`;
}
